import type { FormFields } from '../../../models/newSupportCall';
import { getTasAdditionalRequiredFields, type RequiredFieldRule } from './tasAdditionalRequiredFields';

export type NewSupportCallFormErrors = Partial<Record<keyof FormFields, string>>;

const MIN_PHONE_DIGITS = 10;
const CHASSIS_LENGTH = 17;
const ADMINISTRATIVE_SUBJECTS = ['ADMINISTRATIVE', 'FINANCIAL', 'PORTAL', 'COMMERCIAL'];

const isBlank = (value: unknown) => {
    if (value == null) return true;
    if (typeof value === 'string') return value.trim().length === 0;
    return false;
};

const addRequiredErrors = (
    errors: NewSupportCallFormErrors,
    formData: FormFields,
    rules: RequiredFieldRule[],
) => {
    rules.forEach(({ field, message }) => {
        if (isBlank(formData[field])) {
            errors[field] = message;
        }
    });
};

const validateChassis = (
    errors: NewSupportCallFormErrors,
    chassis: string | undefined,
    isRequired: boolean,
) => {
    if (!chassis) {
        if (isRequired) {
            errors.chassis = 'Chassi do veículo deve ter 17 caracteres';
        }
        return;
    }

    if (chassis.length !== CHASSIS_LENGTH) {
        errors.chassis = 'Chassi do veículo deve ter 17 caracteres';
    }
};

const getPhoneDigitCount = (phone?: string) => (phone ?? '').replace(/\D/g, '').length;

const commonRequiredFields: RequiredFieldRule[] = [
    { field: 'dn', message: 'DN é obrigatório' },
    { field: 'regionalId', message: 'Regional é obrigatório' },
];

const technicalRequiredFields: RequiredFieldRule[] = [
    { field: 'dnResponsible', message: 'Responsável pelo chamado no DN é obrigatório' },
    { field: 'licensePlate', message: 'Placa do veículo é obrigatório' },
    { field: 'customer', message: 'Nome do cliente é obrigatório' },
    { field: 'hourMeter', message: 'Horímetro é obrigatório' },
    { field: 'odometer', message: 'Km é obrigatório' },
    { field: 'entryDate', message: 'Data de entrada é obrigatória' },
    { field: 'partName', message: 'Nome da peça causadora é obrigatório' },
    { field: 'customerComment', message: 'Reclamação do cliente é obrigatório' },
    { field: 'diagnose', message: 'Diagnóstico / causa da falha é obrigatório' },
    { field: 'repair', message: 'Reparo necessário é obrigatório' },
    { field: 'analysisReason', message: 'Motivo do envio para análise é obrigatório' },
];

const cancellationRequiredFields: RequiredFieldRule[] = [
    { field: 'contractNumber', message: 'Número do contrato é obrigatório' },
    { field: 'vehicleQtd', message: 'Quantidade de veículos é obrigatório' },
    { field: 'cancelReason', message: 'Motivo do cancelamento é obrigatório' },
    { field: 'cancelComment', message: 'Solicitação é obrigatório' },
];

const administrativeRequiredFields: RequiredFieldRule[] = [
    { field: 'customerComment', message: 'Detalhe da solicitação é obrigatório' },
];

export const validateNewSupportCallForm = (
    formData: FormFields,
    loggedUserName: string,
): NewSupportCallFormErrors => {
    const errors: NewSupportCallFormErrors = {};
    const subject = formData.subject ?? '';
    const isTechnicalSubject = subject === 'TECHNICAL';

    if (!loggedUserName && isBlank(formData.responsible)) {
        errors.responsible = 'Responsável é obrigatório';
    }

    addRequiredErrors(errors, formData, commonRequiredFields);

    if (isBlank(formData.phone)) {
        errors.phone = 'Telefone é obrigatório';
    } else if (getPhoneDigitCount(formData.phone) < MIN_PHONE_DIGITS) {
        errors.phone = 'Telefone inválido';
    }

    validateChassis(errors, formData.chassis, isTechnicalSubject);

    if (isTechnicalSubject) {
        addRequiredErrors(errors, formData, technicalRequiredFields);
        return errors;
    }

    if (ADMINISTRATIVE_SUBJECTS.includes(subject)) {
        addRequiredErrors(errors, formData, administrativeRequiredFields);
        return errors;
    }

    if (subject === 'CONTRACT_CANCELLATION') {
        addRequiredErrors(errors, formData, cancellationRequiredFields);
        return errors;
    }

    addRequiredErrors(errors, formData, getTasAdditionalRequiredFields(subject));
    return errors;
};

export const getNewSupportCallValidationSummary = (errors: NewSupportCallFormErrors) =>
    Object.values(errors).filter(Boolean).join('; ');
